import React from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { formatCurrency } from '../../../components/common';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const topProducts = [
  { name: 'Modular Kitchen', revenue: 1200000, units: 18, growth: '+12%', color: '#f9a825' },
  { name: 'Wardrobe', revenue: 680000, units: 14, growth: '+8%', color: '#0f172a' },
  { name: 'Full Home Furniture', revenue: 750000, units: 6, growth: '+22%', color: '#3b82f6' },
  { name: 'Bedroom Set', revenue: 320000, units: 8, growth: '-4%', color: '#10b981' },
  { name: 'TV & Study Units', revenue: 180000, units: 10, growth: '+5%', color: '#8b5cf6' },
];

const leadSources = [
  { label: 'Website', value: 18, color: '#0f172a' },
  { label: 'Phone', value: 12, color: '#334155' },
  { label: 'Walk-in', value: 8, color: '#f9a825' },
  { label: 'Social', value: 6, color: '#3b82f6' },
  { label: 'Referral', value: 4, color: '#10b981' },
];
const totalLeadSources = leadSources.reduce((s, l) => s + l.value, 0);

export const SalesPerformanceSection = ({ analytics }) => {
  const monthlyRevenue = analytics?.monthlyRevenue || [];
  const target = 350000;

  // Revenue vs Target chart
  const revenueVsTargetData = {
    labels: MONTHS,
    datasets: [
      {
        label: 'Revenue',
        data: monthlyRevenue,
        backgroundColor: monthlyRevenue.map((v) => (v >= target ? '#f9a825' : '#0f172a')),
        borderRadius: 6,
        barPercentage: 0.55,
      },
      {
        label: 'Target',
        data: MONTHS.map(() => target),
        type: 'line',
        borderColor: '#ef4444',
        borderDash: [6, 4],
        borderWidth: 1.5,
        pointRadius: 0,
        fill: false,
      },
    ],
  };

  const revenueVsTargetOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 10,
          font: { size: 10, family: "'DM Sans', sans-serif" },
          color: '#64748b',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.9)',
        titleFont: { size: 11, weight: 'bold' },
        bodyFont: { size: 11 },
        padding: 10,
        cornerRadius: 6,
        callbacks: {
          label: (ctx) => ` ${formatCurrency(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        grid: { color: 'rgba(0,0,0,0.04)', borderDash: [4, 4] },
        ticks: {
          font: { size: 10, family: "'DM Sans', sans-serif" },
          color: '#94a3b8',
          callback: (v) => `₹${(v / 100000).toFixed(0)}L`,
        },
        border: { display: false },
      },
      x: {
        grid: { display: false },
        ticks: { font: { size: 10, family: "'DM Sans', sans-serif" }, color: '#94a3b8' },
        border: { display: false },
      },
    },
  };

  // KPI data
  const kpis = [
    {
      label: 'Total Revenue',
      value: formatCurrency(analytics?.totalRevenue || 0),
      sub: 'FY 2024',
      trend: '+18%',
      up: true,
      icon: '💰',
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-100',
    },
    {
      label: 'Net Profit',
      value: formatCurrency(analytics?.netProfit || 0),
      sub: `${Math.round(((analytics?.netProfit || 0) / (analytics?.totalRevenue || 1)) * 100)}% margin`,
      trend: '+11%',
      up: true,
      icon: '📈',
      bg: 'bg-emerald-50',
      iconBg: 'bg-emerald-100',
    },
    {
      label: 'Pending Payments',
      value: formatCurrency(analytics?.pendingPayments || 0),
      sub: 'To be collected',
      trend: '-5%',
      up: false,
      icon: '⏳',
      bg: 'bg-orange-50',
      iconBg: 'bg-orange-100',
    },
    {
      label: 'Material Costs',
      value: formatCurrency(analytics?.materialCosts || 0),
      sub: `${Math.round(((analytics?.materialCosts || 0) / (analytics?.totalRevenue || 1)) * 100)}% of revenue`,
      trend: '+3%',
      up: false,
      icon: '🏗️',
      bg: 'bg-blue-50',
      iconBg: 'bg-blue-100',
    },
  ];

  return (
    <div className="space-y-5">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800 font-sans tracking-tight">Sales Performance</h2>
          <p className="text-xs text-slate-400 mt-0.5">Overview of revenue, targets & product performance</p>
        </div>
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400 border border-slate-200 rounded-md px-3 py-1">FY 2024</span>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <div key={kpi.label} className={`card p-5 ${kpi.bg}`}>
            <div className="flex items-start justify-between mb-3">
              <div className={`w-9 h-9 rounded-xl ${kpi.iconBg} flex items-center justify-center text-lg`}>
                {kpi.icon}
              </div>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${kpi.up ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                {kpi.trend}
              </span>
            </div>
            <div className="text-xl font-bold text-slate-900 font-sans tracking-tight leading-tight">{kpi.value}</div>
            <div className="text-[11px] font-semibold text-slate-700 mt-0.5">{kpi.label}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Revenue vs Target + Top Products */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Revenue vs Target Chart */}
        <div className="card p-5 lg:col-span-2">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-800 font-sans tracking-tight">Revenue vs Target</h3>
            <p className="text-xs text-slate-400 mt-0.5">Monthly revenue against ₹3.5L target — <span className="text-amber-500 font-semibold">Gold = achieved</span></p>
          </div>
          <div style={{ height: 230 }}>
            <Bar data={revenueVsTargetData} options={revenueVsTargetOptions} />
          </div>
        </div>

        {/* Top Products */}
        <div className="card p-5">
          <div className="mb-4">
            <h3 className="text-base font-semibold text-slate-800 font-sans tracking-tight">Top Products</h3>
            <p className="text-xs text-slate-400 mt-0.5">By revenue this year</p>
          </div>
          <div className="space-y-3">
            {topProducts.map((p, i) => {
              const maxRev = topProducts[0].revenue;
              const pct = Math.round((p.revenue / maxRev) * 100);
              return (
                <div key={p.name}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-slate-400 w-3">{i + 1}</span>
                      <span className="text-[12px] font-semibold text-slate-700 truncate max-w-[110px]">{p.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold text-slate-600">{formatCurrency(p.revenue)}</span>
                      <span className={`text-[10px] font-bold ${p.growth.startsWith('+') ? 'text-emerald-600' : 'text-red-500'}`}>{p.growth}</span>
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: p.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Lead Source Breakdown */}
      <div className="card p-5">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-slate-800 font-sans tracking-tight">Lead Source Breakdown</h3>
          <p className="text-xs text-slate-400 mt-0.5">Where your customers are coming from</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {leadSources.map((src) => {
            const pct = Math.round((src.value / totalLeadSources) * 100);
            return (
              <div key={src.label} className="flex flex-col items-center gap-2">
                <div className="relative w-16 h-16">
                  <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="3.5" />
                    <circle
                      cx="18" cy="18" r="15.9"
                      fill="none"
                      stroke={src.color}
                      strokeWidth="3.5"
                      strokeDasharray={`${pct} ${100 - pct}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[13px] font-bold text-slate-800">{pct}%</span>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-[12px] font-semibold text-slate-700">{src.label}</div>
                  <div className="text-[11px] text-slate-400">{src.value} leads</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
